'use client';

import { motion } from 'motion/react';

import { sectionHeaderReveal } from '@/config/section-header-reveal';
import { useDiscernibleInView } from '@/hooks/useDiscernibleInView';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { easeStandard } from '@/motion/tokens';

import type { SectionHeaderContentTiming } from '@/config/section-header-reveal';

type SectionProseRevealProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * Body timing knobs. Defaults to chapter `sectionHeaderReveal.body`;
   * Introduction passes `introHeaderReveal.body`.
   */
  timing?: SectionHeaderContentTiming['body'];
};

/**
 * Section-header prose — timed fade/rise once the body reveal root is
 * discernible (same model as LocationPin). Parent `[data-section-body-reveal]`
 * is faded by GSAP (scrubbed on intro and chapter headers).
 *
 * Observer ref stays on an outer wrapper so `useDiscernibleInView` does not
 * see the Motion element's own `opacity: 0` and refuse to flip `inView`.
 */
export function SectionProseReveal({
  children,
  className,
  timing = sectionHeaderReveal.body,
}: SectionProseRevealProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useDiscernibleInView<HTMLDivElement>({
    once: true,
    amount: 0.5,
    disabled: prefersReducedMotion,
  });

  if (prefersReducedMotion) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} className={className}>
      <motion.div
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={{
          hidden: { opacity: 0, y: timing.offsetY },
          visible: { opacity: 1, y: 0 },
        }}
        transition={{
          duration: timing.duration,
          ease: easeStandard,
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}
